import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Textarea,
  Chip,
  CircularProgress,
  Input,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@nextui-org/react";
import {
  MdOutlineStarBorder,
  MdDelete,
  MdDeleteOutline,
  MdOutlineArchive,
  MdOutlineUnarchive,
} from "react-icons/md";
import {
  FiArrowLeft,
  FiChevronDown,
  FiChevronUp,
  FiPlus,
  FiFlag,
  FiCalendar,
  FiInfo,
  FiBookOpen,
} from "react-icons/fi";
import { Line } from "react-chartjs-2";
import { useSelector } from "react-redux";

import DebriefCallsTab from "./DebriefCallsTab";
import StarRating from "./StarRating";
import OperatorDebriefNav from "./OperatorDebriefNav";
import { OBJECTIVE_LIBRARY } from "./objectiveLibrary";
import CreateDebriefModal from "./CreateDebriefModal";
import ConfirmModal from "./ConfirmModal";
import { dateInputToISO, isDebriefOverdue, toDateInputValue } from "./debriefUtils";
import Modal from "../../components/Modal";
import { getGradeBackgroundColor } from "../../utils/colorUtils";
import { lineChartBaseOptions, lineDatasetStyle } from "../../utils/chartTheme";
import {
  updateDebrief,
  deleteDebrief,
  setDebriefArchived,
  addDebriefComment,
  updateDebriefComment,
  deleteDebriefComment,
  createObjective,
  disableObjective,
  fetchObjectiveHistory,
} from "../../redux/actions/debriefActions";
import myAxios from "../../utils/axiosWrapper";
import getConfig from "../../conf/config";
import { auth } from "../../conf/firebase";

const DebriefDetail = () => {
  const { t } = useTranslation();
  const { debriefId } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);
  const { currentUser } = auth;

  const [debrief, setDebrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const callsTabRef = useRef(null);
  const [operators, setOperators] = useState([]);
  const [notes, setNotes] = useState("");
  const [evaluations, setEvaluations] = useState({});
  const [comments, setComments] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [operatorDebriefs, setOperatorDebriefs] = useState([]);
  const [status, setStatus] = useState("draft");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Comment form (general comments only)
  const [newComment, setNewComment] = useState("");
  const [addingComment, setAddingComment] = useState(false);

  // Objective grading
  const [objectiveGrades, setObjectiveGrades] = useState({});
  const [expandedObjectives, setExpandedObjectives] = useState({});
  const [objectiveHistory, setObjectiveHistory] = useState(null);

  // Objective form
  const [showObjectiveForm, setShowObjectiveForm] = useState(false);
  const [newObjectiveName, setNewObjectiveName] = useState("");
  const [addingObjective, setAddingObjective] = useState(false);
  const [libraryCategory, setLibraryCategory] = useState(OBJECTIVE_LIBRARY[0].id);

  // Schedule upcoming debrief modal
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);

  // Confirm modal
  const [confirmAction, setConfirmAction] = useState(null);

  // Archive + scheduled date editing
  const [archiving, setArchiving] = useState(false);
  const [isDatePopoverOpen, setIsDatePopoverOpen] = useState(false);
  const [scheduledDateDraft, setScheduledDateDraft] = useState("");
  const [savingScheduledDate, setSavingScheduledDate] = useState(false);

  // Fetch operators
  useEffect(() => {
    if (!currentUser) return;
    const fetchOperators = async () => {
      try {
        const response = await myAxios.get(`${getConfig().API_URL}/audio/api/v1/admin/operators`);
        const operatorMap = response?.data?.[0]?.operators || {};
        const formatted = [];
        Object.keys(operatorMap).forEach((operatorId) => {
          if (operatorId === "no_operator_id") return;
          formatted.push(operatorMap[operatorId]);
        });
        setOperators(formatted);
      } catch (error) {
        console.error("Error fetching operators:", error);
      }
    };
    fetchOperators();
  }, [user]);

  // Fetch debrief data
  useEffect(() => {
    if (!currentUser) return;
    if (!debriefId) return;
    const loadDebrief = async () => {
      setLoading(true);
      // Navigating between an operator's debriefs reuses this component.
      setExpandedObjectives({});
      setObjectiveHistory(null);
      setShowObjectiveForm(false);
      setNewObjectiveName("");
      setNewComment("");
      try {
        const response = await myAxios.get(
          `${getConfig().API_URL}/audio/api/v1/debriefs/${debriefId}`
        );
        const data = response?.data?.[0]?.debrief;
        if (data) {
          setDebrief(data);
          setNotes(data.notes || "");
          setEvaluations(data.evaluations || {});
          setStatus(data.status || "draft");
          setComments(data.comments || []);
          setObjectiveGrades(data.objective_grades || {});
        }
      } catch (error) {
        console.error("Error loading debrief:", error);
      }
      setLoading(false);
    };
    loadDebrief();
  }, [debriefId, user]);

  const loadObjectives = async () => {
    if (!debrief?.operator_id) return;
    try {
      const response = await myAxios.get(
        `${getConfig().API_URL}/audio/api/v1/debriefs/objectives/${
          debrief.operator_id
        }?include_inactive=true`
      );
      setObjectives(response?.data?.[0]?.objectives || []);
    } catch (error) {
      console.error("Error loading objectives:", error);
    }
  };

  useEffect(() => {
    if (!debrief?.operator_id) return;
    loadObjectives();
  }, [debrief?.operator_id]);

  // Every iteration of this operator's debriefs (archived ones included), newest first.
  // Refetched when the status changes so the picker labels stay accurate.
  useEffect(() => {
    if (!debrief?.operator_id) return;
    let cancelled = false;
    const loadOperatorDebriefs = async () => {
      try {
        const response = await myAxios.get(`${getConfig().API_URL}/audio/api/v1/debriefs`, {
          params: { operator_id: debrief.operator_id, archived: "include" },
        });
        if (!cancelled) setOperatorDebriefs(response?.data?.[0]?.debriefs || []);
      } catch (error) {
        console.error("Error loading operator debriefs:", error);
      }
    };
    loadOperatorDebriefs();
    return () => {
      cancelled = true;
    };
  }, [debrief?.operator_id, debrief?.id, status]);

  const loadComments = async () => {
    try {
      const response = await myAxios.get(
        `${getConfig().API_URL}/audio/api/v1/debriefs/${debrief.id}/comments`
      );
      setComments(response?.data?.[0]?.comments || []);
    } catch (error) {
      console.error("Error loading comments:", error);
    }
  };

  const operator = operators?.find((op) => op.id === debrief?.operator_id);
  const kpi = debrief?.kpi_snapshot || {};

  const formatDate = (date) => {
    if (!date) return "-";
    const d = date?.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const formatDateTime = (date) => {
    if (!date) return "-";
    const d = date?.toDate ? date.toDate() : new Date(date);
    return d.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleComplete = async () => {
    setSaving(true);
    try {
      await updateDebrief(debrief.id, {
        notes,
        evaluations,
        status: "completed",
        objective_grades: objectiveGrades,
      });
      setStatus("completed");
    } catch (error) {
      console.error("Error completing debrief:", error);
    }
    setSaving(false);
  };

  const handleStartDebrief = async () => {
    setSaving(true);
    try {
      await updateDebrief(debrief.id, { status: "draft" });
      setStatus("draft");
    } catch (error) {
      console.error("Error starting debrief:", error);
    }
    setSaving(false);
  };

  const handleToggleArchive = async () => {
    const nextArchived = !debrief.archived;
    setArchiving(true);
    try {
      await setDebriefArchived(debrief.id, nextArchived);
      setDebrief((prev) => ({
        ...prev,
        archived: nextArchived,
        archived_at: nextArchived ? new Date().toISOString() : null,
      }));
    } catch (error) {
      console.error("Error archiving debrief:", error);
    }
    setArchiving(false);
  };

  const handleDatePopoverChange = (open) => {
    setIsDatePopoverOpen(open);
    if (open) setScheduledDateDraft(toDateInputValue(debrief?.scheduled_date));
  };

  // value: "yyyy-MM-dd", or "" to remove the scheduled date
  const handleSaveScheduledDate = async (value) => {
    const scheduledDate = dateInputToISO(value);
    setSavingScheduledDate(true);
    try {
      await updateDebrief(debrief.id, { scheduled_date: scheduledDate });
      setDebrief((prev) => ({ ...prev, scheduled_date: scheduledDate }));
      setIsDatePopoverOpen(false);
    } catch (error) {
      console.error("Error updating scheduled date:", error);
    }
    setSavingScheduledDate(false);
  };

  const handleDelete = () => {
    setConfirmAction({
      message: t("Êtes-vous sûr de vouloir supprimer ce debrief ?"),
      confirmLabel: t("Supprimer"),
      destructive: true,
      onConfirm: async () => {
        setConfirmAction(null);
        setDeleting(true);
        try {
          await deleteDebrief(debrief.id);
          navigate("/debriefs");
        } catch (error) {
          console.error("Error deleting debrief:", error);
        }
        setDeleting(false);
      },
    });
  };

  const handleAddGeneralComment = async () => {
    if (!newComment.trim()) return;
    setAddingComment(true);
    try {
      await addDebriefComment(debrief.id, newComment, null, null);
      setNewComment("");
      await loadComments();
    } catch (error) {
      console.error("Error adding comment:", error);
    }
    setAddingComment(false);
  };

  // Called by DebriefCallsTab for call-specific comments
  const handleAddCallComment = async (content, callId, audioTimestamp) => {
    await addDebriefComment(debrief.id, content, callId, audioTimestamp);
    await loadComments();
  };

  const handleUpdateComment = async (commentId, content) => {
    await updateDebriefComment(commentId, content);
    await loadComments();
  };

  const handleDeleteComment = async (commentId) => {
    await deleteDebriefComment(commentId);
    await loadComments();
  };

  const handleAddObjective = async () => {
    if (!newObjectiveName.trim()) return;
    setAddingObjective(true);
    try {
      await createObjective(debrief.operator_id, newObjectiveName, "number", 5);
      setNewObjectiveName("");
      setShowObjectiveForm(false);
      await loadObjectives();
    } catch (error) {
      console.error("Error adding objective:", error);
    }
    setAddingObjective(false);
  };

  const handleDisableObjective = (objectiveId) => {
    setConfirmAction({
      message: t("Désactiver cet objectif ?"),
      destructive: true,
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          await disableObjective(objectiveId);
          await loadObjectives();
        } catch (error) {
          console.error("Error disabling objective:", error);
        }
      },
    });
  };

  const handleObjectiveGradeChange = async (objectiveId, grade) => {
    const previousGrades = objectiveGrades;
    const newGrades = { ...objectiveGrades, [objectiveId]: grade };
    setObjectiveGrades(newGrades);
    try {
      await updateDebrief(debrief.id, { objective_grades: newGrades });
    } catch (error) {
      console.error("Error saving objective grade:", error);
      // Do not leave a grade on screen that was never saved.
      setObjectiveGrades(previousGrades);
    }
  };

  const toggleObjectiveExpand = async (objectiveId) => {
    const isExpanding = !expandedObjectives[objectiveId];
    setExpandedObjectives((prev) => ({ ...prev, [objectiveId]: isExpanding }));
    if (isExpanding && objectiveHistory === null && debrief?.operator_id) {
      const history = await fetchObjectiveHistory(debrief.operator_id);
      setObjectiveHistory(history);
    }
  };

  const getObjectiveChartData = (objectiveId) => {
    if (!objectiveHistory || objectiveHistory.length === 0) return null;
    const labels = objectiveHistory.map((h) => {
      const d = h.date?.toDate ? h.date.toDate() : new Date(h.date);
      return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" });
    });
    const data = objectiveHistory.map((h) => h.objective_grades?.[objectiveId] ?? null);
    return {
      labels,
      datasets: [
        {
          data,
          ...lineDatasetStyle("#7c3aed"),
          spanGaps: true,
        },
      ],
    };
  };

  const objectiveChartOptions = lineChartBaseOptions({
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx) => `${ctx.parsed.y}/5` } },
    },
    scales: {
      y: { min: 0, max: 5, ticks: { stepSize: 1 } },
    },
  });

  const getStatusChip = () => {
    if (status === "completed") {
      return (
        <Chip
          size="sm"
          variant="flat"
          color="success"
        >
          {t("Terminé")}
        </Chip>
      );
    }
    if (status === "scheduled") {
      const isOverdue = isDebriefOverdue({ status, scheduled_date: debrief?.scheduled_date });
      return (
        <Chip
          size="sm"
          variant="flat"
          color={isOverdue ? "danger" : "primary"}
        >
          {isOverdue ? t("En retard") : t("Planifié")}
        </Chip>
      );
    }
    return (
      <Chip
        size="sm"
        variant="flat"
        color="warning"
      >
        {t("Brouillon")}
      </Chip>
    );
  };

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center py-12">
        <CircularProgress
          size="lg"
          aria-label="Loading..."
          color="primary"
        />
      </div>
    );
  }

  if (!debrief) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center py-12">
        <p className="text-gray-500">{t("Debrief non trouvé")}</p>
        <Button
          variant="light"
          color="primary"
          className="mt-4"
          onClick={() => navigate("/debriefs")}
        >
          {t("Retour aux debriefs")}
        </Button>
      </div>
    );
  }

  const avgGrade = kpi.average_grade || -1;
  const avgGradeNormalized = Math.round((avgGrade + Number.EPSILON) * 10) / 10;
  const gradeBgColor = getGradeBackgroundColor(avgGradeNormalized);
  const operatorName = operator
    ? `${operator.first_name} ${operator.last_name}`
    : debrief.operator_id;

  // Merge fetched objectives + snapshot objectives for grading
  // Active objectives always show. Inactive ones show only if they were
  // part of this debrief (in snapshot or already graded).
  const activeObjectives = objectives.filter((o) => o.is_active !== false);
  const inactiveObjectives = objectives.filter((o) => o.is_active === false);
  const relevantInactive = inactiveObjectives.filter(
    (obj) =>
      objectiveGrades[obj.id] != null || debrief?.objectives_snapshot?.some((s) => s.id === obj.id)
  );
  const allObjectives = [
    ...activeObjectives,
    ...relevantInactive,
    ...(debrief?.objectives_snapshot?.filter(
      (snap) =>
        !activeObjectives.find((o) => o.id === snap.id) &&
        !relevantInactive.find((o) => o.id === snap.id)
    ) || []),
  ];

  const renderObjectiveRow = (obj, isActive) => {
    const isExpanded = expandedObjectives[obj.id];
    const chartData = isExpanded ? getObjectiveChartData(obj.id) : null;
    const grade = objectiveGrades[obj.id] || 0;

    return (
      <div
        key={obj.id}
        className={`rounded-lg overflow-hidden shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.10),0px_1px_3px_0px_rgba(0,0,0,0.10)] ${
          isActive ? "bg-white" : "bg-gray-50"
        }`}
      >
        <div
          className="px-6 py-4 flex flex-wrap items-center gap-x-4 gap-y-2 cursor-pointer hover:bg-gray-50/50 transition-colors"
          onClick={() => toggleObjectiveExpand(obj.id)}
          onKeyDown={() => {}}
        >
          <FiFlag
            size={18}
            className="text-gray-400 shrink-0"
          />
          <div className="flex-1 min-w-[10rem]">
            <span className={`text-base ${isActive ? "text-gray-900" : "text-gray-500"}`}>
              {obj.name}
            </span>
            {!isActive && <span className="ml-2 text-xs text-orange-500">({t("Désactivé")})</span>}
          </div>
          <div
            className="flex items-center gap-3"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={() => {}}
          >
            <StarRating
              value={grade}
              onChange={(g) => handleObjectiveGradeChange(obj.id, g)}
            />
          </div>
          <div className="shrink-0 text-gray-400">
            {isExpanded ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
          </div>
          {isActive && (
            <Button
              size="sm"
              variant="light"
              color="danger"
              isIconOnly
              className="shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                handleDisableObjective(obj.id);
              }}
            >
              <MdDelete size={16} />
            </Button>
          )}
        </div>
        {isExpanded && (
          <div className="px-6 pb-5 border-t border-gray-100">
            <p className="text-xs text-gray-400 mt-3 mb-2 uppercase tracking-wide">
              {t("Évolution sur les derniers debriefs")}
            </p>
            {chartData && chartData.datasets[0].data.some((v) => v !== null) ? (
              <div style={{ height: 140 }}>
                <Line
                  data={chartData}
                  options={objectiveChartOptions}
                />
              </div>
            ) : (
              <p className="text-sm text-gray-400 py-4 text-center">
                {t("Pas encore de données d'évolution")}
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full w-full mt-4">
      {/* Header */}
      <div className="flex items-center justify-between px-5 h-16">
        <div className="flex items-end gap-4">
          <Button
            variant="light"
            isIconOnly
            onClick={() => navigate("/debriefs")}
            size="sm"
          >
            <FiArrowLeft size={18} />
          </Button>
          <h1 className="text-4xl text-primary leading-10">{operatorName}</h1>
          <span className="text-sm text-gray-800">
            {formatDate(debrief?.period_start)} - {formatDate(debrief?.period_end)}
          </span>
          {getStatusChip()}
          {debrief?.archived && (
            <Chip
              size="sm"
              variant="flat"
              startContent={<MdOutlineArchive size={14} />}
            >
              {t("Archivé")}
            </Chip>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Scheduled date (editable) */}
          {(debrief?.scheduled_date || status === "scheduled") && (
            <Popover
              placement="bottom-end"
              isOpen={isDatePopoverOpen}
              onOpenChange={handleDatePopoverChange}
            >
              <PopoverTrigger>
                <button
                  type="button"
                  aria-label={t("Modifier la date planifiée")}
                  className="h-11 p-3 bg-white rounded-lg flex items-center gap-2.5 hover:bg-gray-50 transition-colors"
                >
                  <FiCalendar
                    size={16}
                    className={
                      isDebriefOverdue({ status, scheduled_date: debrief?.scheduled_date })
                        ? "text-red-500"
                        : "text-gray-500"
                    }
                  />
                  <span className="text-sm text-gray-500">
                    {debrief?.scheduled_date
                      ? formatDate(debrief.scheduled_date)
                      : t("Choisir une date")}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="p-4 w-[260px]">
                <div className="w-full flex flex-col gap-3">
                  <span className="text-sm font-semibold text-gray-900">{t("Date planifiée")}</span>
                  <Input
                    type="date"
                    aria-label={t("Date planifiée")}
                    placeholder=" "
                    value={scheduledDateDraft}
                    onChange={(e) => setScheduledDateDraft(e.target.value)}
                  />
                  <div className="flex justify-end gap-2">
                    {status !== "scheduled" && debrief?.scheduled_date && (
                      <Button
                        size="sm"
                        variant="light"
                        color="danger"
                        isDisabled={savingScheduledDate}
                        onClick={() => handleSaveScheduledDate("")}
                      >
                        {t("Retirer")}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      color="primary"
                      isLoading={savingScheduledDate}
                      isDisabled={!scheduledDateDraft}
                      onClick={() => handleSaveScheduledDate(scheduledDateDraft)}
                    >
                      {t("Enregistrer")}
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          )}
          {status === "scheduled" && (
            <button
              type="button"
              className="px-4 py-2.5 bg-white rounded-lg shadow-drop-100 outline outline-1 outline-offset-[-1px] outline-neutral-200 flex items-center gap-2 hover:bg-gray-50 transition-colors"
              onClick={handleStartDebrief}
              disabled={saving}
            >
              <span className="text-base font-semibold text-gray-900">
                {t("Commencer le debrief")}
              </span>
            </button>
          )}
          <button
            type="button"
            className="px-4 py-2.5 bg-white rounded-lg shadow-drop-100 outline outline-1 outline-offset-[-1px] outline-neutral-200 flex items-center gap-2 hover:bg-gray-50 transition-colors"
            onClick={() => setIsScheduleModalOpen(true)}
          >
            <span className="text-base font-semibold text-gray-900">
              {t("Planifier un prochain debrief")}
            </span>
          </button>
          {status === "draft" && (
            <button
              type="button"
              className="px-4 py-2.5 bg-primary rounded-lg shadow-drop-100 flex items-center gap-2 hover:bg-primary/90 transition-colors"
              onClick={handleComplete}
              disabled={saving}
            >
              <span className="text-base font-semibold text-white">
                {t("Marquer comme terminé")}
              </span>
            </button>
          )}
          <Tooltip content={debrief?.archived ? t("Désarchiver") : t("Archiver")}>
            <Button
              isIconOnly
              variant="light"
              aria-label={debrief?.archived ? t("Désarchiver") : t("Archiver")}
              className="h-11 w-11 bg-white text-gray-600"
              isDisabled={archiving}
              onClick={handleToggleArchive}
            >
              {debrief?.archived ? (
                <MdOutlineUnarchive size={20} />
              ) : (
                <MdOutlineArchive size={20} />
              )}
            </Button>
          </Tooltip>
          <Tooltip content={t("Supprimer")}>
            <Button
              isIconOnly
              variant="light"
              color="danger"
              aria-label={t("Supprimer le debrief")}
              className="h-11 w-11 bg-white"
              isDisabled={deleting}
              onClick={handleDelete}
            >
              <MdDeleteOutline size={20} />
            </Button>
          </Tooltip>
        </div>
      </div>

      <OperatorDebriefNav
        debriefs={operatorDebriefs}
        currentId={debrief?.id}
        onNavigate={(id) => navigate(`/debriefs/${id}`)}
        formatDate={formatDate}
      />

      {/* Two-column content */}
      <div className="flex-1 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 h-full">
          {/* ===== LEFT COLUMN ===== */}
          <div className="overflow-auto p-5 space-y-6">
            {/* Section title */}
            <h2 className="text-2xl text-primary">{t("Debriefing")}</h2>

            {/* KPI Card */}
            <div className="bg-white rounded-lg shadow-[0px_1px_3px_0px_rgba(0,0,0,0.10)] p-6">
              <div className="flex items-center justify-between pb-4 border-b border-gray-200 mb-6">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <h3 className="text-2xl font-bold text-gray-900">
                      {t("KPIs de")} {operator?.first_name || ""}
                    </h3>
                    <Tooltip
                      content={t(
                        "Les KPIs sont calculés sur l'ensemble des appels de l'opérateur sur la période sélectionnée"
                      )}
                      placement="right"
                      className="max-w-xs"
                    >
                      <span className="text-gray-400 cursor-help">
                        <FiInfo size={16} />
                      </span>
                    </Tooltip>
                  </div>
                  <p className="text-base text-gray-500">
                    {formatDate(debrief?.period_start)} - {formatDate(debrief?.period_end)}
                  </p>
                </div>
              </div>

              {/* Stats grid */}
              <div className="grid grid-cols-2">
                {/* Representative Grade */}
                <div className="p-2.5 border-r border-b border-gray-200">
                  <div className="flex flex-col gap-1">
                    <div className="text-2xl font-bold text-gray-900">
                      {avgGrade >= 0 && (
                        <div className="flex">
                          <div
                            className={`flex items-center px-2 py-0.5 rounded-md text-sm ${gradeBgColor}`}
                          >
                            <MdOutlineStarBorder size={16} />
                            <span className="ml-0.5">{avgGradeNormalized}</span>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">{t("Note moyenne")}</div>
                  </div>
                </div>
                {/* Number of Calls */}
                <div className="p-2.5 border-b border-gray-200">
                  <div className="flex flex-col gap-1">
                    <div className="text-2xl font-bold text-gray-900">{kpi.total_calls || 0}</div>
                    <div className="text-sm text-gray-500">{t("Nombre d'appels")}</div>
                  </div>
                </div>
                {/* Orange Calls */}
                <div className="p-2.5 border-r border-b border-gray-200">
                  <div className="flex flex-col gap-1">
                    <div className="text-2xl font-bold text-gray-900">{kpi.orange_calls || 0}</div>
                    <div className="text-sm text-gray-500">{t("Appels oranges")}</div>
                  </div>
                </div>
                {/* Red Calls */}
                <div className="p-2.5 border-b border-gray-200">
                  <div className="flex flex-col gap-1">
                    <div className="text-2xl font-bold text-gray-900">{kpi.red_calls || 0}</div>
                    <div className="text-sm text-gray-500">{t("Appels rouges")}</div>
                  </div>
                </div>
                {/* Green Calls */}
                <div className="p-2.5 border-r border-gray-200">
                  <div className="flex flex-col gap-1">
                    <div className="text-2xl font-bold text-gray-900">{kpi.green_calls || 0}</div>
                    <div className="text-sm text-gray-500">{t("Appels verts")}</div>
                  </div>
                </div>
                {/* Frustrated Clients */}
                <div className="p-2.5">
                  <div className="flex flex-col gap-1">
                    <div className="text-2xl font-bold text-gray-900">
                      {kpi.frustrated_clients || 0}
                    </div>
                    <div className="text-sm text-gray-500">{t("Clients frustrés")}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Objectives */}
            <div className="space-y-2">
              <div className="flex items-center justify-between mb-1">
                <h3 className="text-gray-900">{t("Objectifs")}</h3>
                {showObjectiveForm && (
                  <button
                    type="button"
                    className="text-sm text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
                    onClick={() => setShowObjectiveForm(false)}
                  >
                    {t("Annuler")}
                  </button>
                )}
              </div>

              {allObjectives.length === 0 ? (
                <div className="bg-white rounded-lg shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.10),0px_1px_3px_0px_rgba(0,0,0,0.10)] text-center text-gray-400 py-8 text-sm">
                  {t("Aucun objectif défini pour le moment")}
                </div>
              ) : (
                <div className="space-y-2">
                  {allObjectives.map((obj) =>
                    renderObjectiveRow(
                      obj,
                      activeObjectives.some((o) => o.id === obj.id)
                    )
                  )}
                </div>
              )}

              {/* Add objective button - full width */}
              {!showObjectiveForm && (
                <button
                  type="button"
                  className="w-full h-11 p-4 bg-indigo-600/20 rounded-lg outline outline-1 outline-offset-[-0.50px] outline-primary flex justify-center items-center gap-2 hover:bg-indigo-600/30 transition-colors cursor-pointer"
                  onClick={() => setShowObjectiveForm(true)}
                >
                  <FiPlus
                    size={14}
                    className="text-primary"
                  />
                  <span className="text-sm font-medium text-primary">
                    {t("Ajouter un objectif")}
                  </span>
                </button>
              )}

              {showObjectiveForm && (
                <div className="bg-white rounded-lg shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.10),0px_1px_3px_0px_rgba(0,0,0,0.10)] p-4 space-y-4">
                  <div className="flex gap-3 items-end">
                    <Input
                      label={t("Nom de l'objectif")}
                      value={newObjectiveName}
                      onChange={(e) => setNewObjectiveName(e.target.value)}
                      placeholder={t("ex : Améliorer la qualité des appels")}
                      size="sm"
                      className="flex-1"
                    />
                    <Button
                      color="primary"
                      size="sm"
                      onClick={handleAddObjective}
                      isLoading={addingObjective}
                      isDisabled={!newObjectiveName.trim()}
                      className="shrink-0"
                    >
                      {t("Enregistrer")}
                    </Button>
                  </div>

                  {/* Library: ideas to start from. Picking one fills the field, still editable. */}
                  <div>
                    <p className="text-sm text-gray-900 mb-2 flex items-center gap-2">
                      <FiBookOpen
                        size={14}
                        className="text-gray-400"
                      />
                      {t("Besoin d'inspiration ? Choisissez dans la bibliothèque")}
                    </p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {OBJECTIVE_LIBRARY.map((category) => (
                        <button
                          key={category.id}
                          type="button"
                          className={`px-3 py-1 rounded-full text-sm transition-colors cursor-pointer ${
                            libraryCategory === category.id
                              ? "bg-primary text-white"
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                          onClick={() => setLibraryCategory(category.id)}
                        >
                          {t(category.label)}
                        </button>
                      ))}
                    </div>
                    <div className="space-y-2">
                      {OBJECTIVE_LIBRARY.find((c) => c.id === libraryCategory).objectives.map(
                        (item) => {
                          const name = t(item.name);
                          const alreadyAdded = activeObjectives.some(
                            (o) => o.name?.trim().toLowerCase() === name.toLowerCase()
                          );
                          return (
                            <button
                              key={item.name}
                              type="button"
                              disabled={alreadyAdded}
                              className="w-full text-left rounded-lg border border-gray-200 px-4 py-3 hover:border-primary hover:bg-primary/5 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-default disabled:hover:border-gray-200 disabled:hover:bg-transparent"
                              onClick={() => setNewObjectiveName(name)}
                            >
                              <span className="block text-sm text-gray-900">
                                {name}
                                {alreadyAdded && (
                                  <span className="ml-2 text-xs text-gray-400">
                                    ({t("Déjà ajouté")})
                                  </span>
                                )}
                              </span>
                              <span className="block text-xs text-gray-500 mt-0.5">
                                {t(item.hint)}
                              </span>
                            </button>
                          );
                        }
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Message History / Comments */}
            <div className="bg-white rounded-lg shadow-[0px_1px_3px_0px_rgba(0,0,0,0.10)] p-6">
              <h3 className="text-2xl text-primary mb-6">{t("Historique des messages")}</h3>

              {/* Read-only comments timeline */}
              {comments.length > 0 ? (
                <div className="mb-6">
                  {comments.map((comment, idx) => (
                    <div
                      key={comment.id}
                      className="flex"
                    >
                      {/* Timeline column: dot + line */}
                      <div className="flex flex-col items-center w-8 shrink-0">
                        <div className="w-6 h-6 rounded-full bg-stone-300 ring-[3px] ring-white shrink-0" />
                        {idx < comments.length - 1 && <div className="w-px flex-1 bg-gray-200" />}
                      </div>
                      {/* Content */}
                      <div className="flex-1 pl-3 pb-10">
                        <div className="bg-white rounded-lg shadow-[0px_1px_2px_0px_rgba(0,0,0,0.08)] border border-gray-200 p-4">
                          <div className="flex flex-col gap-1 mb-4">
                            <div className="text-xs text-gray-400">
                              {formatDateTime(comment.created_at)}
                            </div>
                            <div className="text-sm text-gray-600">
                              <span className="font-medium text-gray-900">
                                {comment.author_name}
                              </span>
                            </div>
                          </div>
                          <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
                            {comment.call_id && (
                              <button
                                type="button"
                                className="flex items-center gap-2 text-xs text-gray-400 mb-2 hover:text-primary transition-colors cursor-pointer"
                                onClick={() =>
                                  callsTabRef.current?.seekToComment(
                                    comment.call_id,
                                    comment.audio_timestamp || 0
                                  )
                                }
                              >
                                <span className="inline-block w-4 h-4 rounded-full bg-primary/20" />
                                <span className="font-mono">
                                  #{comment.call_id.substring(0, 7)}
                                </span>
                                {comment.audio_timestamp != null && (
                                  <span>
                                    {Math.floor(comment.audio_timestamp / 60)}:
                                    {String(Math.floor(comment.audio_timestamp % 60)).padStart(
                                      2,
                                      "0"
                                    )}
                                  </span>
                                )}
                              </button>
                            )}
                            <p className="text-xs text-gray-500 whitespace-pre-wrap">
                              {comment.content}
                            </p>
                            {comment.is_edited && (
                              <span className="text-xs text-gray-400 mt-1 inline-block">
                                ({t("modifié")})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 mb-4">
                  {t("Aucun commentaire pour le moment")}
                </p>
              )}

              {/* General comment form */}
              <div className="space-y-4">
                <div className="rounded-lg">
                  <Textarea
                    placeholder={t("Ajouter un commentaire...")}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    minRows={3}
                    size="sm"
                    classNames={{ inputWrapper: "border-0 shadow-none" }}
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    color="primary"
                    size="sm"
                    className="rounded-lg"
                    onClick={handleAddGeneralComment}
                    isLoading={addingComment}
                    isDisabled={!newComment.trim()}
                  >
                    {t("Envoyer")}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* ===== RIGHT COLUMN ===== */}
          <div className="overflow-auto p-5">
            <div className="flex items-center gap-2 mb-6">
              <h2 className="text-2xl text-primary">{t("Appels attachés")}</h2>
              <Tooltip
                content={t(
                  "Ajoutez ici des appels spécifiques de l'opérateur pour les écouter, en discuter et les commenter"
                )}
                placement="left"
                className="max-w-xs"
              >
                <span className="text-gray-400 cursor-help">
                  <FiInfo size={16} />
                </span>
              </Tooltip>
            </div>
            <DebriefCallsTab
              ref={callsTabRef}
              debriefId={debrief.id}
              comments={comments}
              onAddComment={handleAddCallComment}
              onUpdateComment={handleUpdateComment}
              onDeleteComment={handleDeleteComment}
            />
          </div>
        </div>
      </div>

      {/* Schedule upcoming debrief modal */}
      {isScheduleModalOpen && (
        <Modal
          isOpen={isScheduleModalOpen}
          showFooter={false}
          size="2xl"
          handleOnClose={() => setIsScheduleModalOpen(false)}
        >
          <CreateDebriefModal
            operators={operators}
            defaultOperatorId={debrief?.operator_id || ""}
            onClose={() => setIsScheduleModalOpen(false)}
            onSuccess={(newDebriefId) => {
              setIsScheduleModalOpen(false);
              navigate(`/debriefs/${newDebriefId}`);
            }}
          />
        </Modal>
      )}

      {/* Confirm modal */}
      {confirmAction && (
        <ConfirmModal
          message={confirmAction.message}
          confirmLabel={confirmAction.confirmLabel}
          destructive={confirmAction.destructive}
          onConfirm={confirmAction.onConfirm}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </div>
  );
};

export default DebriefDetail;
